import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuction } from '../../context/AuctionContext';
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    Alert,
    CardContent,
    Avatar,
    Button,
    Link,
    Tooltip,
    useMediaQuery
} from '@mui/material';
import { seasonPlayersCollection } from '../../services/players';
import { db } from '../../firebase.js';
import { collection, getDocs, query, where, doc, onSnapshot, orderBy, limit, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import PersonIcon from '@mui/icons-material/Person';
import PlayerDetailsModal from '../../components/PlayerDetailsModal';
import BudgetTeamsModal from '../../components/BudgetTeamsModal';
import BidsOverviewModal from '../../components/BidsOverviewModal';
import DraftOfferModal from '../../components/DraftOfferModal';
import PlayerCard from '../../components/PlayerCard';
import SidelinedIcon from '../../components/SidelinedIcon';
import { useSidelinedMap } from '../../hooks/useSidelinedMap';
import { page } from '../../theme/commonStyles';
import { REFUNDED_TRANSFER_TYPES } from '../../constants/game';

const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

function MyTeam() {
    const { user } = useAuth();
    const { auctionId } = useAuction();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [teamDetails, setTeamDetails] = useState(null);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [pointsData, setPointsData] = useState({});
    const [auction, setAuction] = useState(null);
    const [activeBlindPoolRound, setActiveBlindPoolRound] = useState(null);
    const [myBids, setMyBids] = useState([]);
    const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
    const [isBidsOverviewModalOpen, setIsBidsOverviewModalOpen] = useState(false);
    const [currentRoundCaptain, setCurrentRoundCaptain] = useState(null);
    const [liveRankingData, setLiveRankingData] = useState(null);

    // Draft state
    const [draftConfig, setDraftConfig] = useState(null);
    const [draftOfferings, setDraftOfferings] = useState([]);
    const [isDraftOfferModalOpen, setIsDraftOfferModalOpen] = useState(false);
    const [playerToOffer, setPlayerToOffer] = useState(null);
    const [draftPickedByMap, setDraftPickedByMap] = useState(new Map()); // playerId → { drafterName }

    const sidelinedMap = useSidelinedMap(auction?.season);

    useEffect(() => {
        if (!user || !auctionId) {
            setLoading(false);
            return;
        }

        setLoading(true);



        const unsubAuction = onSnapshot(doc(db, 'auctions', auctionId), (docSnap) => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            } else {
                setError("Veiling niet gevonden.");
            }
        }, (err) => {
            console.error("Error listening to auction data:", err);
            setError('Kon veiling details niet live bijwerken.');
        });

        const participantDocRef = doc(db, 'auctions', auctionId, 'participants', user.uid);
        const unsubParticipant = onSnapshot(participantDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const participantData = docSnap.data();
                setTeamDetails(prev => ({
                    ...prev,
                    budget: participantData.budget,
                    coach: participantData.coach || '',
                }));
            } else {
                setTeamDetails({ acquisitions: {}, budget: 0, coach: '', transfersLeft: 0 });
            }
        }, (err) => {
            console.error("Error listening to participant data:", err);
            setError('Kon team details niet live bijwerken.');
        });

        const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc'));
        const unsubTransfers = onSnapshot(transfersQuery, async (transfersSnap) => {
            const myPlayersMap = new Map();
            const myAcquisitions = {};

            const allTransfers = transfersSnap.docs.map(d => d.data());
            allTransfers.forEach(transfer => {
                if (transfer.to === user.uid) {
                    myPlayersMap.set(transfer.playerId, { id: transfer.playerId, name: transfer.playerName });
                    myAcquisitions[transfer.playerId] = transfer.amount;
                }
                if (transfer.from === user.uid && transfer.to !== user.uid) {
                    myPlayersMap.delete(transfer.playerId);
                    delete myAcquisitions[transfer.playerId];
                }
            });

            // Bereken transfersLeft dynamisch (zelfde logica als backend)
            const getTotalAllowed = (phase) => {
                const preAuction = ['Voorbereiding', 'Veiling', 'Blinde Pool'];
                const preKnockout = ['Poulefase', 'Tussenronde', 'Draft'];
                const knockout = ['Knock-out', 'Finaleronde'];
                if (preAuction.includes(phase)) return 12;
                if (preKnockout.includes(phase)) return 15;
                if (knockout.includes(phase)) return 19;
                return 0;
            };
            const currentPhase = auction?.spelfase || '';
            const totalAllowed = getTotalAllowed(currentPhase);
            const excludedTypes = ['coach-change', 'Draft'];
            const transfersUsed = allTransfers.filter(t => t.to === user.uid && !excludedTypes.includes(t.type)).length;
            const nonClTransfersCount = allTransfers.filter(t => t.from === user.uid && REFUNDED_TRANSFER_TYPES.includes(t.type)).length;
            const computedTransfersLeft = totalAllowed - transfersUsed + nonClTransfersCount;

            setTeamDetails(prev => ({ ...prev, acquisitions: myAcquisitions, transfersLeft: computedTransfersLeft }));

            const playerIds = Array.from(myPlayersMap.keys());

            if (playerIds.length === 0) {
                setTeamPlayers([]);
                setPointsData({});
                setLoading(false);
                return;
            }

            // De veiling komt via een aparte snapshot binnen; zonder seizoen is er
            // geen spelerscollectie om in te zoeken. Deze listener vuurt opnieuw
            // zodra de veiling er wel is.
            if (!auction?.season) {
                return;
            }

            try {
                const playerIdsAsNumbers = playerIds.map(id => Number(id));
                const playersQuery = query(seasonPlayersCollection(auction.season), where('id', 'in', playerIdsAsNumbers));
                const playersSnap = await getDocs(playersQuery);
                const fetchedPlayers = playersSnap.docs.map(doc => doc.data());

                const translatedPlayers = fetchedPlayers.map(p => ({ ...p, position: POSITION_MAP[p.position] || p.position }));
                setTeamPlayers(translatedPlayers);

                // --- START OPTIMIZED POINT CALCULATION ---
                const calculatedPoints = {};

                if (auction?.season && playerIds.length > 0) {
                    // 1. Get all rounds each player was in the participant's team
                    const playersPerRoundQuery = query(
                        collection(db, 'auctions', auctionId, 'playersPerParticipantPerRound'),
                        where('participantId', '==', user.uid),
                        where('playerId', 'in', playerIds)
                    );
                    const playersPerRoundSnap = await getDocs(playersPerRoundQuery);

                    // Build a map of playerId -> Set of roundIds, and collect all unique rounds
                    const playerRoundsMap = new Map(); // Map<playerId, Set<roundId>>
                    const allUniqueRounds = new Set();
                    
                    playersPerRoundSnap.docs.forEach(doc => {
                        const data = doc.data();
                        if (!playerRoundsMap.has(data.playerId)) {
                            playerRoundsMap.set(data.playerId, new Set());
                        }
                        playerRoundsMap.get(data.playerId).add(data.roundId);
                        allUniqueRounds.add(data.roundId);
                    });

                    // Initialize calculated points for all players
                    playerIds.forEach(playerId => {
                        calculatedPoints[playerId] = {};
                    });

                    // 2. Fetch all scores per round in PARALLEL (one query per round, not per player)
                    if (allUniqueRounds.size > 0) {
                        const roundScoresPromises = [...allUniqueRounds].map(async (roundId) => {
                            const scoresQuery = query(
                                collection(db, `playerScores/${auction.season}/rounds/${roundId}/scores`),
                                where('playerId', 'in', playerIdsAsNumbers)
                            );
                            const scoresSnap = await getDocs(scoresQuery);
                            return { roundId, docs: scoresSnap.docs };
                        });

                        const roundScoresResults = await Promise.all(roundScoresPromises);

                        // 3. Process all scores and assign to the correct players
                        roundScoresResults.forEach(({ roundId, docs }) => {
                            docs.forEach(scoreDoc => {
                                const scoreData = scoreDoc.data();
                                const playerId = String(scoreData.playerId);
                                
                                // Only count points if the player was in the team during this round
                                if (playerRoundsMap.get(playerId)?.has(roundId)) {
                                    if (scoreData.totalPoints) {
                                        if (!calculatedPoints[playerId][roundId]) {
                                            calculatedPoints[playerId][roundId] = 0;
                                        }
                                        calculatedPoints[playerId][roundId] += scoreData.totalPoints;
                                    }
                                }
                            });
                        });
                    }
                }

                setPointsData(calculatedPoints);
                // --- END OPTIMIZED POINT CALCULATION ---

            } catch (err) {
                console.error("Fout bij het ophalen van spelerdetails of punten:", err);
                setError('Kon spelerdetails of punten niet bijwerken.');
            } finally {
                setLoading(false);
            }
        }, (err) => {
            console.error("Error listening to transfers:", err);
            setError('Kon transfers niet live bijwerken.');
            setLoading(false);
        });

        const blindPoolRoundsQuery = query(
            collection(db, 'auctions', auctionId, 'blindPoolRounds'),
            where('status', '==', 'open'),
            limit(1)
        );

        const unsubBlindPoolRounds = onSnapshot(blindPoolRoundsQuery, (snapshot) => {
            if (!snapshot.empty) {
                setActiveBlindPoolRound({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
            } else {
                setActiveBlindPoolRound(null);
            }
        }, (err) => {
            console.error("Error fetching active blind pool round:", err);
        });

        return () => {
            unsubAuction();
            unsubParticipant();
            unsubTransfers();
            unsubBlindPoolRounds();
        };
    }, [user, auctionId, auction?.season, auction?.spelfase]); // season + spelfase: transferlimiet hangt van de fase af

    // Fetch live ranking data
    useEffect(() => {
        if (!user || !auctionId) return;

        const liveRankingRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRanking');
        const unsub = onSnapshot(liveRankingRef, (docSnap) => {
            if (docSnap.exists()) {
                const ranking = docSnap.data().ranking || [];
                const myRankingData = ranking.find(r => r.uid === user.uid);
                setLiveRankingData(myRankingData);
            } else {
                setLiveRankingData(null);
            }
        });

        return () => unsub();
    }, [user, auctionId]);

    // Fetch current round captain
    useEffect(() => {
        if (!user || !auctionId) {
            setCurrentRoundCaptain(null);
            return;
        }

        const unsubMatchSelections = onSnapshot(doc(db, 'auctions', auctionId, 'matchSelections', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const selections = docSnap.data().selections || {};
                // Find the most recent captain selection
                let latestCaptain = null;
                let latestMatchId = null;

                for (const [matchId, selection] of Object.entries(selections)) {
                    if (selection.captain && (!latestMatchId || matchId > latestMatchId)) {
                        latestCaptain = selection.captain;
                        latestMatchId = matchId;
                    }
                }
                setCurrentRoundCaptain(latestCaptain);
            } else {
                setCurrentRoundCaptain(null);
            }
        });

        return () => unsubMatchSelections();
    }, [user, auctionId]);

    useEffect(() => {
        if (!activeBlindPoolRound?.id || !user?.uid || !auctionId) {
            setMyBids([]);
            return;
        }

        const myBidsQuery = query(
            collection(db, 'auctions', auctionId, 'blindPoolRounds', activeBlindPoolRound.id, 'bids'),
            where('participantId', '==', user.uid),
            orderBy('timestamp', 'desc')
        );

        const unsubscribe = onSnapshot(myBidsQuery, (bidsSnapshot) => {
            setMyBids(bidsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => unsubscribe();
    }, [activeBlindPoolRound, user, auctionId]);

    // Luister naar draft config en offerings
    useEffect(() => {
        if (!user || !auctionId) return;

        const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
        const unsubDraftConfig = onSnapshot(draftConfigRef, (docSnap) => {
            if (docSnap.exists()) {
                setDraftConfig(docSnap.data());
            } else {
                setDraftConfig(null);
            }
        });

        const offeringsQuery = query(
            collection(db, 'auctions', auctionId, 'draft', 'config', 'offerings'),
            where('offeredByUid', '==', user.uid)
        );
        const unsubOfferings = onSnapshot(offeringsQuery, (snapshot) => {
            setDraftOfferings(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Luister naar Draft transfers waar ik de 'from' ben (mijn aangeboden spelers die gekozen zijn)
        const draftTransfersQuery = query(
            collection(db, 'auctions', auctionId, 'auctionTransfers'),
            where('type', '==', 'Draft'),
            where('from', '==', user.uid)
        );
        const unsubDraftTransfers = onSnapshot(draftTransfersQuery, async (snapshot) => {
            const pickedMap = new Map();
            const drafterUids = new Set();
            snapshot.docs.forEach(d => {
                const transfer = d.data();
                drafterUids.add(transfer.to);
                pickedMap.set(String(transfer.playerId), { drafterUid: transfer.to, drafterName: null });
            });

            // Haal deelnemernamen op
            if (drafterUids.size > 0) {
                const participantsSnap = await getDocs(collection(db, 'auctions', auctionId, 'participants'));
                const nameMap = new Map();
                participantsSnap.docs.forEach(d => {
                    const data = d.data();
                    nameMap.set(data.uid || d.id, data.name || 'Onbekend');
                });
                pickedMap.forEach((value, key) => {
                    value.drafterName = nameMap.get(value.drafterUid) || 'Onbekend';
                });
            }

            setDraftPickedByMap(new Map(pickedMap));
        });

        return () => {
            unsubDraftConfig();
            unsubOfferings();
            unsubDraftTransfers();
        };
    }, [user, auctionId]);

    const handlePlayerClick = (player) => {
        const playerPointsData = pointsData[player.id] || {};
        setSelectedPlayer({ ...player, points: playerPointsData });
    };

    const handleCloseModal = () => {
        setSelectedPlayer(null);
    };

    const getTotalPoints = (playerId) => {
        const playerPointsData = pointsData[playerId];
        if (!playerPointsData) return 0;
        const totalPoints = Object.values(playerPointsData).reduce((sum, points) => sum + points, 0);

        // Apply captain multiplier if this player is the current captain
        if (currentRoundCaptain === String(playerId)) {
            return Math.round(totalPoints * 1.5);
        }

        return totalPoints;
    };

    const handleDeleteBid = async (bidId) => {
        if (!activeBlindPoolRound) return;
        const bidDocRef = doc(db, 'auctions', auctionId, 'blindPoolRounds', activeBlindPoolRound.id, 'bids', bidId);
        try {
            await deleteDoc(bidDocRef);
        } catch (err) {
            console.error("Error deleting bid:", err);
            setError("Fout bij het verwijderen van het bod.");
        }
    };

    // --- Draft offering handlers ---
    const isEliminatedParticipant = draftConfig?.eliminatedParticipants?.includes(user?.uid) && auction?.spelfase === 'Draft';

    const handleOpenDraftOfferModal = (player) => {
        setPlayerToOffer(player);
        setIsDraftOfferModalOpen(true);
    };

    const handleCloseDraftOfferModal = () => {
        setPlayerToOffer(null);
        setIsDraftOfferModalOpen(false);
    };

    const handleConfirmDraftOffer = async (player, profitPercentage) => {
        if (!player || !user || !auctionId) return;
        try {
            const offeringRef = doc(db, 'auctions', auctionId, 'draft', 'config', 'offerings', String(player.id));
            await setDoc(offeringRef, {
                playerId: String(player.id),
                playerName: player.name,
                clubName: player.clubName || '',
                clubLogo: player.clubLogo || null,
                position: player.position || '',
                photoUrl: player.photoUrl || null,
                offeredByUid: user.uid,
                offeredByName: user.displayName || user.email || '',
                profitPercentage: profitPercentage,
                offeredAt: serverTimestamp()
            });
            handleCloseDraftOfferModal();
        } catch (err) {
            console.error('Error offering player for draft:', err);
            setError('Fout bij het beschikbaar stellen van de speler.');
        }
    };

    const handleWithdrawDraftOffer = async (playerId) => {
        if (!user || !auctionId) return;
        try {
            const offeringRef = doc(db, 'auctions', auctionId, 'draft', 'config', 'offerings', String(playerId));
            await deleteDoc(offeringRef);
        } catch (err) {
            console.error('Error withdrawing draft offer:', err);
            setError('Fout bij het intrekken van het aanbod.');
        }
    };

    const totalSpent = teamDetails?.acquisitions ? Object.values(teamDetails.acquisitions).reduce((sum, price) => sum + price, 0) : 0;

    const composition = teamPlayers.reduce((acc, player) => {
        acc[player.position] = (acc[player.position] || 0) + 1;
        return acc;
    }, { 'Coach': 0, 'Keeper': 0, 'Verdediger': 0, 'Middenvelder': 0, 'Aanvaller': 0 });

    const rules = {
        'Totaal': { min: 12, max: 12, current: teamPlayers.length },
        'Coach': { min: 1, max: 1, current: composition['Coach'] },
        'Keepers': { min: 1, max: 1, current: composition['Keeper'] },
        'Verdedigers': { min: 3, max: 5, current: composition['Verdediger'] },
        'Middenvelders': { min: 3, max: 5, current: composition['Middenvelder'] },
        'Aanvallers': { min: 2, max: 4, current: composition['Aanvaller'] },
    };

    const positionOrder = { 'Coach': 0, 'Keeper': 1, 'Verdediger': 2, 'Middenvelder': 3, 'Aanvaller': 4 };

    const rosterToRender = teamPlayers.slice().sort((a, b) => {
        return positionOrder[a.position] - positionOrder[b.position];
    });

    // Lege plekken ("placeholders") voor een nog niet compleet team. Per linie tonen
    // we zoveel plekken als je er nog kunt vullen tot het maximum, maar begrensd door
    // de plekken die niet al gereserveerd zijn voor de ongehaalde minimums van de
    // andere linies. Zo verdwijnen de placeholders bij andere linies zodra de laatste
    // plek(ken) verplicht naar één linie moeten.
    const lineDefs = [
        { position: 'Coach', rule: rules['Coach'] },
        { position: 'Keeper', rule: rules['Keepers'] },
        { position: 'Verdediger', rule: rules['Verdedigers'] },
        { position: 'Middenvelder', rule: rules['Middenvelders'] },
        { position: 'Aanvaller', rule: rules['Aanvallers'] },
    ];
    const remainingTotal = rules['Totaal'].max - rules['Totaal'].current;
    const totalMandatory = lineDefs.reduce((sum, l) => sum + Math.max(0, l.rule.min - l.rule.current), 0);
    const openSlots = [];
    if (remainingTotal > 0) {
        lineDefs.forEach((l) => {
            const roomInLine = l.rule.max - l.rule.current;
            if (roomInLine <= 0) return;
            const mandatoryHere = Math.max(0, l.rule.min - l.rule.current);
            const otherMandatory = totalMandatory - mandatoryHere;
            const fillable = Math.max(0, Math.min(roomInLine, remainingTotal - otherMandatory));
            for (let i = 0; i < fillable; i++) {
                openSlots.push({ __placeholder: true, position: l.position, id: `__slot_${l.position}_${i}` });
            }
        });
    }

    // Echte spelers en lege plekken samen, gesorteerd per linie (spelers eerst binnen
    // een linie dankzij de stabiele sort).
    const rosterWithSlots = [...rosterToRender, ...openSlots].sort(
        (a, b) => positionOrder[a.position] - positionOrder[b.position]
    );

    const getRowStyle = (item) => {
        const position = item.position;
        return theme.positions[position]?.backgroundColor || 'transparent';
    };

    // Lege plek als mobiel kaartje: dezelfde vorm als een spelerkaartje, maar
    // gestippeld en gedempt met een plus-icoon in plaats van een foto.
    const renderSlotCard = (slot) => (
        <Box
            key={slot.id}
            sx={{
                border: '1px dashed',
                borderColor: 'rgba(255, 255, 255, 0.25)',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: theme.positions[slot.position]?.backgroundColor || 'transparent',
            }}
        >
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    border: '1px dashed rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Lege plek
                        </Typography>
                        <Box sx={{
                            ...theme.positions[slot.position],
                            color: 'common.white',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            flexShrink: 0,
                            opacity: 0.85,
                        }}>
                            {slot.position.charAt(0)}
                        </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                        {slot.position}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    // Lege plek als tabelrij (desktop), met dezelfde kolommen als een spelerrij.
    const renderSlotRow = (slot) => (
        <TableRow key={slot.id} sx={{ bgcolor: theme.positions[slot.position]?.backgroundColor || 'transparent' }}>
            <TableCell component="th" scope="row">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        border: '1px dashed rgba(255, 255, 255, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Box>
                    <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Lege plek</Typography>
                </Box>
            </TableCell>
            <TableCell><Chip label={slot.position} size="small" sx={{ ...theme.positions[slot.position], opacity: 0.7 }} /></TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
            {isEliminatedParticipant && <TableCell />}
        </TableRow>
    );

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!teamDetails) {
        return <Typography>Selecteer een veiling om je team te zien.</Typography>;
    }

    const showPointsCard = auction?.spelfase !== 'Voorbereiding' && auction?.spelfase !== 'Veiling' && auction?.spelfase !== 'Blinde Pool';
    const showBudgetInfo = ['Voorbereiding', 'Veiling', 'Blinde Pool'].includes(auction?.spelfase);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Team</Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                {showBudgetInfo && (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                                        Uitgegeven
                                    </Typography>
                                    <Typography variant="h5" component="div">
                                        €{totalSpent}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                                        Te besteden
                                    </Typography>
                                    <Typography variant="h5" component="div">
                                        €{teamDetails.budget}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
                {showPointsCard && (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                                        Wedstrijdpunten
                                    </Typography>
                                    <Typography variant="h5" component="div" color="primary">
                                        {liveRankingData?.matchPoints || 0} pnt
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                                        Scorepunten
                                    </Typography>
                                    <Typography variant="h5" component="div">
                                        {(liveRankingData?.playerPoints || 0) +
                                            (liveRankingData?.tacticPoints || 0) +
                                            (liveRankingData?.tacticCounter || 0)} pnt
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>Transfers</Typography>
                            <Typography variant="h5" component="div">
                                {teamDetails.transfersLeft || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {auction?.spelfase === 'Blinde Pool' && (
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h5" gutterBottom>Mijn Blinde Pool Biedingen</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Hieronder zie je de biedingen die je hebt geplaatst in de huidige ronde. Je kunt biedingen verwijderen zolang de ronde open is.
                        Je kunt nieuwe biedingen plaatsen op de <Link component={RouterLink} to="../transfer-market">Transfermarkt</Link> pagina.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button variant="outlined" onClick={() => setIsBudgetModalOpen(true)}>Budget teams</Button>
                        <Button variant="outlined" onClick={() => setIsBidsOverviewModalOpen(true)}>Overzicht biedingen</Button>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Naam</TableCell>
                                    <TableCell>Club</TableCell>
                                    <TableCell>Positie</TableCell>
                                    <TableCell align="right">Bedrag</TableCell>
                                    <TableCell align="right">Actie</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {myBids.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">Je hebt nog geen biedingen geplaatst.</TableCell>
                                    </TableRow>
                                ) : (
                                    myBids.map((bid) => {
                                        const translatedPosition = POSITION_MAP[bid.playerPosition] || bid.playerPosition;

                                        return (
                                            <TableRow key={bid.id}>
                                                <TableCell>{bid.playerName}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        {bid.playerClubLogo && (
                                                            <Box
                                                                component="img"
                                                                src={bid.playerClubLogo}
                                                                alt={`${bid.playerClub} logo`}
                                                                sx={{ height: 24, width: 24, objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        {bid.playerClub}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{translatedPosition}</TableCell>
                                                <TableCell align="right">{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(bid.amount)}</TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleDeleteBid(bid.id)}
                                                    >
                                                        Verwijder
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Draft: spelers beschikbaar stellen (alleen voor uitgeschakelde deelnemers) */}
            {isEliminatedParticipant && (
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h5" gutterBottom>Draft - Spelers beschikbaar stellen</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Je bent uitgeschakeld voor de knock-outfase. Je kunt maximaal 3 spelers beschikbaar stellen voor de draft.
                        Geef per speler een winstpercentage op (0-15%). Dit percentage van het prijzengeld gaat naar jou als de koper wint.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {draftOfferings.length}/3 spelers beschikbaar gesteld
                    </Alert>

                    {/* Huidige offerings */}
                    {draftOfferings.length > 0 && (
                        <>
                            {isMobile ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                    {draftOfferings.map((offering) => {
                                        const pickedBy = draftPickedByMap.get(String(offering.playerId));
                                        return (
                                            <PlayerCard
                                                key={offering.id}
                                                player={{
                                                    name: offering.playerName,
                                                    photoUrl: offering.photoUrl,
                                                    clubLogo: offering.clubLogo,
                                                    clubName: offering.clubName,
                                                    position: offering.position,
                                                }}
                                                status={sidelinedMap.get(String(offering.playerId)) || null}
                                                subCaption={pickedBy && (
                                                    <Typography variant="caption" sx={{ color: 'success.main', display: 'block', lineHeight: 1.2 }}>
                                                        Gekozen door {pickedBy.drafterName}
                                                    </Typography>
                                                )}
                                                rightSlot={
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                        <Chip label={`${offering.profitPercentage}%`} size="small" color={pickedBy ? 'success' : 'default'} variant="outlined" />
                                                        {!pickedBy && (
                                                            <Tooltip title={draftConfig?.status === 'active' ? 'Kan niet intrekken terwijl de draft actief is' : ''}>
                                                                <span>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="error"
                                                                        size="small"
                                                                        onClick={() => handleWithdrawDraftOffer(offering.playerId)}
                                                                        disabled={draftConfig?.status === 'active'}
                                                                        sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}
                                                                    >
                                                                        Intrekken
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        );
                                    })}
                                </Box>
                            ) : (
                                <TableContainer component={Paper} sx={{ mb: 2 }}>
                                    <Table aria-label="aangeboden spelers tabel">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Speler</TableCell>
                                                <TableCell>Positie</TableCell>
                                                <TableCell>Club</TableCell>
                                                <TableCell align="right">Winstpercentage</TableCell>
                                                <TableCell>Gekozen door</TableCell>
                                                <TableCell align="right">Actie</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {draftOfferings.map((offering) => {
                                                const dutchPosition = POSITION_MAP[offering.position] || offering.position;
                                                const pickedBy = draftPickedByMap.get(String(offering.playerId));
                                                return (
                                                    <TableRow key={offering.id} hover>
                                                        <TableCell component="th" scope="row">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar src={offering.photoUrl} alt={offering.playerName}><SportsSoccerIcon /></Avatar>
                                                                {offering.playerName}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={dutchPosition} size="small" sx={theme.positions[dutchPosition]} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                {offering.clubLogo && (
                                                                    <Box
                                                                        component="img"
                                                                        src={offering.clubLogo}
                                                                        alt={`${offering.clubName} logo`}
                                                                        sx={{ height: 24, width: 24, objectFit: 'contain' }}
                                                                    />
                                                                )}
                                                                {offering.clubName}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">{offering.profitPercentage}%</TableCell>
                                                        <TableCell>
                                                            {pickedBy ? (
                                                                <Chip label={pickedBy.drafterName} size="small" color="success" />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {pickedBy ? (
                                                                <Chip label="Gekozen" size="small" color="success" variant="outlined" />
                                                            ) : (
                                                                <Tooltip title={draftConfig?.status === 'active' ? 'Kan niet intrekken terwijl de draft actief is' : ''}>
                                                                    <span>
                                                                        <Button
                                                                            variant="outlined"
                                                                            color="error"
                                                                            size="small"
                                                                            onClick={() => handleWithdrawDraftOffer(offering.playerId)}
                                                                            disabled={draftConfig?.status === 'active'}
                                                                        >
                                                                            Intrekken
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Klik op een speler in je team hieronder om deze beschikbaar te stellen.
                    </Typography>
                </Box>
            )}

            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rosterWithSlots.map((item) => {
                        if (item.__placeholder) return renderSlotCard(item);
                        const player = item;
                        const isCaptain = currentRoundCaptain === String(player.id);

                        return (
                            <PlayerCard
                                key={player.id}
                                player={player}
                                isCaptain={isCaptain}
                                status={sidelinedMap.get(String(player.id)) || null}
                                bgcolor={getRowStyle(player)}
                                onClick={() => handlePlayerClick(player)}
                                rightSlot={
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" sx={{ color: isCaptain ? 'primary.main' : 'inherit' }}>
                                            {getTotalPoints(player.id)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            €{teamDetails.acquisitions[player.id]}
                                        </Typography>
                                    </Box>
                                }
                                footer={isEliminatedParticipant && (
                                    <Box sx={{ p: 1.5, pt: 0, display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                                        {draftOfferings.some(o => o.playerId === String(player.id)) ? (
                                            <Chip label={`Aangeboden (${draftOfferings.find(o => o.playerId === String(player.id))?.profitPercentage || 0}%)`} color="success" size="small" />
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleOpenDraftOfferModal(player)}
                                                disabled={draftOfferings.length >= 3}
                                            >
                                                Aanbieden voor Draft
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            />
                        );
                    })}
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table aria-label="mijn team tabel">
                        <TableHead>
                            <TableRow>
                                <TableCell>Speler</TableCell>
                                <TableCell>Positie</TableCell>
                                <TableCell>Club</TableCell>
                                <TableCell align="right">Aankoopwaarde</TableCell>
                                <TableCell align="right">Punten</TableCell>
                                {isEliminatedParticipant && <TableCell align="right">Draft</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rosterWithSlots.map((item) => {
                                if (item.__placeholder) return renderSlotRow(item);
                                const player = item;
                                const rowStyle = getRowStyle(player);
                                const isCaptain = currentRoundCaptain === String(player.id);

                                return (
                                    <TableRow
                                        key={player.id}
                                        hover
                                        onClick={() => handlePlayerClick(player)}
                                        sx={{ ...rowStyle, cursor: 'pointer' }}
                                    >
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 'normal' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar src={player.photoUrl} alt={player.name}><SportsSoccerIcon /></Avatar>
                                                {player.name} {isCaptain && '(c)'}
                                                <SidelinedIcon status={sidelinedMap.get(String(player.id)) || null} />
                                            </Box>
                                        </TableCell>
                                        <TableCell><Chip label={player.position} size="small" sx={theme.positions[player.position]} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                {player.clubLogo && (
                                                    <Box
                                                        component="img"
                                                        src={player.clubLogo}
                                                        alt={`${player.clubName} logo`}
                                                        sx={{ height: 24, width: 24, objectFit: 'contain' }}
                                                    />
                                                )}
                                                {player.clubName}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">€{teamDetails.acquisitions[player.id]}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: isCaptain ? 'primary.main' : 'inherit' }}>
                                            {getTotalPoints(player.id)}
                                            {isCaptain && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                    (1.5x aanvoerder)
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {isEliminatedParticipant && (
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                {draftOfferings.some(o => o.playerId === String(player.id)) ? (
                                                    <Chip label={`${draftOfferings.find(o => o.playerId === String(player.id))?.profitPercentage || 0}%`} color="success" size="small" />
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleOpenDraftOfferModal(player)}
                                                        disabled={draftOfferings.length >= 3}
                                                    >
                                                        Aanbieden
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}


            <Card sx={{ mt: 4, mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Teamsamenstelling
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {Object.entries(rules).map(([key, { min, max, current }]) => {
                            const isViolated = current < min || current > max;
                            return (
                                <Grid item xs={6} sm={4} md={2.4} key={key}>
                                    <Typography variant="body2" color="text.secondary">{key}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="h6" component="span" color={isViolated ? 'error.main' : 'text.primary'}>
                                            {current}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            / {min === max ? max : `${min}-${max}`}
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </CardContent>
            </Card>

            {selectedPlayer && (
                <PlayerDetailsModal
                    player={selectedPlayer}
                    status={selectedPlayer ? (sidelinedMap.get(String(selectedPlayer.id)) || null) : null}
                    open={Boolean(selectedPlayer)}
                    onClose={handleCloseModal}
                />
            )}

            <BudgetTeamsModal open={isBudgetModalOpen} onClose={() => setIsBudgetModalOpen(false)} />
            <BidsOverviewModal open={isBidsOverviewModalOpen} onClose={() => setIsBidsOverviewModalOpen(false)} />
            <DraftOfferModal
                open={isDraftOfferModalOpen}
                onClose={handleCloseDraftOfferModal}
                player={playerToOffer}
                onConfirm={handleConfirmDraftOffer}
                currentOfferCount={draftOfferings.length}
            />
        </Box>
    );
}

export default MyTeam;